<template>
  <div class="ems-import">
    <div class="ems-import-intro">
      <div class="ems-import-icon">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"/>
        </svg>
      </div>
      <h4>Sync from 1EMS &amp; Odoo</h4>
      <p>
        Pulls open MOs and their steps from 1EMS, and confirmed customer POs
        from Odoo — linked to their supply MOs by project code.
      </p>
    </div>

    <div class="date-row">
      <label for="ems-optimizer-start-date">Optimizer start date</label>
      <input
        id="ems-optimizer-start-date"
        v-model="optimizerStartDate"
        type="date"
        :disabled="busy"
      />
      <span class="hint">Steps scheduled before this date stay locked in place.</span>
    </div>

    <div v-if="syncStatus === 'success' && summary" class="result success">
      <span>
        Synced {{ summary.jobs }} MOs · {{ summary.operations }} steps ·
        {{ summary.salesOrders?.lines ?? 0 }} PO lines linked<template
          v-if="summary.salesOrders?.unmatched"> ({{ summary.salesOrders.unmatched }} lines unmatched)</template><template
          v-if="summary.capacitySeeded"> · {{ summary.capacitySeeded }} work centers seeded</template>
      </span>
    </div>
    <div v-if="syncStatus === 'error'" class="result error"><span>{{ syncMessage }}</span></div>

    <div v-if="optimizeStatus === 'success'" class="result success"><span>{{ optimizeMessage }}</span></div>
    <div v-if="optimizeStatus === 'error'" class="result error"><span>{{ optimizeMessage }}</span></div>

    <div class="button-row">
      <button class="btn primary" :disabled="busy" @click="runSync">
        {{ syncStatus === 'syncing' ? 'Syncing…' : 'Sync Odoo' }}
      </button>
      <button
        class="btn secondary"
        :disabled="busy || syncStatus !== 'success'"
        title="Re-run the scheduler over the freshly synced MOs"
        @click="runOptimizer"
      >
        {{ optimizeStatus === 'optimizing' ? 'Optimizing…' : 'Run Optimizer' }}
      </button>
      <button class="btn ghost" :disabled="busy" @click="emit('close')">Close</button>
    </div>
  </div>
</template>

<script setup lang="ts">
// BridgeInternal's import window — injected into Core's Import Data modal via the
// tenant pack (components.importWindow). Contract with Core: emit 'close' to
// dismiss, 'synced' whenever server data changed so Core reloads the view.
import { ref, computed } from 'vue';

const emit = defineEmits<{ (e: 'close'): void; (e: 'synced'): void }>();

interface SyncSummary {
  jobs: number;
  operations: number;
  capacitySeeded?: number;
  salesOrders?: { lines: number; matched: number; unmatched: number };
}

const optimizerStartDate = ref(new Date().toISOString().slice(0, 10));
const syncStatus = ref<'idle' | 'syncing' | 'success' | 'error'>('idle');
const syncMessage = ref('');
const summary = ref<SyncSummary | null>(null);
const optimizeStatus = ref<'idle' | 'optimizing' | 'success' | 'error'>('idle');
const optimizeMessage = ref('');

const busy = computed(() => syncStatus.value === 'syncing' || optimizeStatus.value === 'optimizing');

const baseUrl = import.meta.env.VITE_API_BASE_URL || '';

async function postJson(path: string, body: unknown): Promise<any> {
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || data.details || `HTTP ${res.status}`);
  }
  return data;
}

async function runSync() {
  syncStatus.value = 'syncing';
  syncMessage.value = '';
  optimizeStatus.value = 'idle';
  try {
    summary.value = await postJson('/api/ems-scheduler-sync/run', {
      optimizerStartDate: optimizerStartDate.value,
    });
    syncStatus.value = 'success';
    emit('synced');
  } catch (err) {
    syncStatus.value = 'error';
    syncMessage.value = err instanceof Error ? err.message : 'Sync failed';
  }
}

async function runOptimizer() {
  optimizeStatus.value = 'optimizing';
  optimizeMessage.value = '';
  try {
    const result = await postJson('/api/optimize', {});
    const placed = Array.isArray(result.scheduleUpdates) ? result.scheduleUpdates.length : 0;
    optimizeStatus.value = 'success';
    optimizeMessage.value = `Optimizer placed ${placed} steps`;
    emit('synced');
  } catch (err) {
    optimizeStatus.value = 'error';
    optimizeMessage.value = err instanceof Error ? err.message : 'Optimization failed';
  }
}
</script>

<style scoped>
.ems-import {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ems-import-intro {
  text-align: center;
  color: #334155;
}
.ems-import-icon {
  color: #64748b;
  margin-bottom: 6px;
}
.ems-import-intro h4 {
  margin: 0 0 4px;
  font-size: 16px;
}
.ems-import-intro p {
  margin: 0 auto;
  max-width: 420px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}
.date-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.date-row label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.date-row input {
  width: 180px;
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
}
.date-row .hint {
  font-size: 12px;
  color: #94a3b8;
}
.result {
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
}
.result.success {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.result.error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}
.button-row {
  display: flex;
  gap: 10px;
}
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn.primary {
  background: #1d4ed8;
  color: #fff;
}
.btn.secondary {
  background: #fff;
  color: #1d4ed8;
  border-color: #1d4ed8;
}
.btn.ghost {
  background: #fff;
  color: #64748b;
  border-color: #cbd5e1;
  margin-left: auto;
}
</style>
